// @ts-check

if (!customElements.get('ff-header-nav-buttons')) {
  class FfHeaderNavButtons extends HTMLElement {
    /** @type {HTMLElement|null} */
    ffTabsGroup = null;

    /** @type {HTMLElement|null} */
    ffCollectionsGroup = null;

    /** @type {HTMLElement|null} */
    ffTargetElement = null;

    /** @type {boolean} */
    ffIsPastSentinel = false;

    /** @type {(() => void)|null} */
    ffScrollHandler = null;

    /** @type {ResizeObserver|null} */
    ffResizeObserver = null;

    constructor() {
      super();

      this.ffTabsGroup = this.querySelector('[data-group="tabs"]');
      this.ffCollectionsGroup = this.querySelector('[data-group="collections"]');

      this.ffInitHeightMeasurement();
      this.ffInitScrollListener();
      this.ffInitTabClicks();
      this.ffInitSliders();
      
    }

    /**
     * Initialize height measurement for sticky positioning.
     * Sets CSS variable --ff-collection-nav-height on :root for other elements to use.
     * Uses ResizeObserver to track height changes (content load, resize, etc.).
     */
    ffInitHeightMeasurement = () => {
      const section = this.closest('[class*="shopify-section"]');
      if (!section) return;

      const updateHeight = () => {
        const height = section.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--ff-collection-nav-height', `${height}px`);
      };

      // Use ResizeObserver for accurate tracking of height changes
      this.ffResizeObserver = new ResizeObserver(updateHeight);
      this.ffResizeObserver.observe(section);

      // Initial measurement
      updateHeight();
    };

    /**
     * Initialize scroll listener for group switching.
     * When scrolled past target element's top edge = show collections.
     * When above target element's top edge = show tabs.
     * Only active if there are valid tabs (data-has-valid-tabs="true").
     */
    ffInitScrollListener = () => {
      // Only init scroll behavior if we have valid tabs
      // If no valid tabs, collections are always visible (no switching needed)
      const hasValidTabs = this.dataset.hasValidTabs === 'true';
      if (!hasValidTabs) return;

      const sentinelSelector = this.dataset.sentinelSelector;
      if (!sentinelSelector) return;

      const targetElement = document.querySelector(sentinelSelector);
      if (!targetElement) return;

      // Only init if we have both groups
      if (!this.ffTabsGroup || !this.ffCollectionsGroup) return;

      this.ffTargetElement = /** @type {HTMLElement} */ (targetElement);

      // Scroll handler - checks if nav-buttons bottom edge has reached target's top edge
      this.ffScrollHandler = () => {
        if (!this.ffTargetElement) return;

        // Get this component's bottom edge (sticky header = constant viewport position)
        const navButtonsBottom = this.getBoundingClientRect().bottom;
        // Get target's top edge relative to viewport (changes with scroll)
        const targetTop = this.ffTargetElement.getBoundingClientRect().top;

        // When target's top reaches nav-buttons bottom = switch to collections
        const wasPast = this.ffIsPastSentinel;
        this.ffIsPastSentinel = targetTop <= navButtonsBottom;

        // Only update DOM if state changed
        if (wasPast !== this.ffIsPastSentinel) {
          this.ffUpdateGroupVisibility();
        }
      };

      // Listen to scroll with passive for performance
      window.addEventListener('scroll', this.ffScrollHandler, { passive: true });

      // Initial check
      this.ffScrollHandler();
    };

    /**
     * Update visibility of tab and collection groups based on scroll position.
     */
    ffUpdateGroupVisibility = () => {
      if (!this.ffTabsGroup || !this.ffCollectionsGroup) return;

      if (this.ffIsPastSentinel) {
        // Past sentinel: hide tabs, show collections
        this.ffTabsGroup.classList.add('ff-nav-buttons__group--hidden');
        this.ffCollectionsGroup.classList.remove('ff-nav-buttons__group--hidden');
      } else {
        // Before sentinel: show tabs, hide collections
        this.ffTabsGroup.classList.remove('ff-nav-buttons__group--hidden');
        this.ffCollectionsGroup.classList.add('ff-nav-buttons__group--hidden');
      }
    };

    /**
     * Initialize tab click handlers.
     * Clicking a tab removes all other tabs' filter values and adds the clicked tab's value.
     */
    ffInitTabClicks = () => {
      if (!this.ffTabsGroup) return;

      // Get all tab buttons
      /** @type {NodeListOf<HTMLElement>} */
      const tabs = this.ffTabsGroup.querySelectorAll('[data-ff-tab-param]');

      tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          this.ffHandleTabClick(tab, tabs);
        });
      });
    };

    /**
     * Handle tab click: update URL params and dispatch event to ff-facets.
     * @param {HTMLElement} clickedTab - The clicked tab element
     * @param {NodeListOf<HTMLElement>} allTabs - All tab elements
     */
    ffHandleTabClick = (clickedTab, allTabs) => {
      const clickedParam = clickedTab.dataset.ffTabParam;
      const clickedValue = clickedTab.dataset.ffTabValue;

      if (!clickedParam || !clickedValue) return;

      // Build new URL
      const newUrl = new URL(window.location.href);

      // Remove all tabs' filter values from URL
      allTabs.forEach((tab) => {
        const param = tab.dataset.ffTabParam;
        const value = tab.dataset.ffTabValue;
        if (param && value) {
          newUrl.searchParams.delete(param, value);
        }
      });

      // Add clicked tab's value
      newUrl.searchParams.append(clickedParam, clickedValue);

      // Update active states visually
      allTabs.forEach((tab) => {
        const button = tab.closest('.ff-button') || tab;
        button.classList.remove('ff-button-active');
      });
      const clickedButton = clickedTab.closest('.ff-button') || clickedTab;
      clickedButton.classList.add('ff-button-active');

      // Show shimmer/loading state on section container (includes block + logo)
      const sectionContainer = this.closest('[class*="shopify-section"]') || this.closest('[class*="ff-collection-nav"]');
      if (sectionContainer) {
        sectionContainer.classList.add('ff-shimmer');
      }

      // Dispatch custom event for ff-facets to handle
      const event = new CustomEvent('ff-filter-tab:change', {
        bubbles: true,
        detail: { url: newUrl }
      });
      this.dispatchEvent(event);
    };

    disconnectedCallback() {
      if (this.ffScrollHandler) {
        window.removeEventListener('scroll', this.ffScrollHandler);
        this.ffScrollHandler = null;
      }
      if (this.ffResizeObserver) {
        this.ffResizeObserver.disconnect();
        this.ffResizeObserver = null;
      }
      this.ffTargetElement = null;
    }

    /**
     * Initialize Splide sliders if display mode is slider.
     * Mounts Splide on each .splide element found inside groups.
     */
    ffInitSliders = () => {
      const splideEls = this.querySelectorAll('.splide');
      if (!splideEls.length) return;

      const mount = () => {
        splideEls.forEach(el => {
          if (el.splide) return;
          const opts = JSON.parse(el.dataset.splide || '{}');
          new Splide(el, opts).mount();
        });
      };

      if (window.ffSplide) {
        window.ffSplide().then(mount);
      } else if (typeof Splide !== 'undefined') {
        mount();
      } else {
        // Edge sin loader global: esperar a que Splide aparezca
        window.addEventListener('DOMContentLoaded', mount);
      }
    }
  }

  customElements.define('ff-header-nav-buttons', FfHeaderNavButtons);
}

